import React from 'react';
import { I18n } from 'common/i18n';

// components
import { ForgeIcon, ForgeIconButton, ForgeMenu } from '@tylertech/forge-react';
import { IMenuOption, IMenuSelectEventData } from '@tylertech/forge';
import { picklistSizingStrategy } from 'common/components/Dropdown/picklistSizingStrategy';
import Dropdown, { DropdownSize } from 'common/components/Forms/Dropdown';
import SocrataIcon, { IconName } from 'common/components/SocrataIcon';
import { isTableAdvancedTotalingEnabled } from 'common/visualizations/helpers/VifSelectors';

// project constants
const scope = 'shared.visualizations.panes.data';

interface TableHierarchyGroupingKebabMenuProps {
  duplicationAllowed: boolean;
  removeAllowed: boolean;
  handleOnDuplicateHierarchy: () => void;
  handleOnRemoveHierarchy: () => void;
  handleRename?: () => void;
}

enum HierarchyMenuOption {
  DUPLICATE,
  RESET,
  REMOVE,
  RENAME,
}

interface HierarchyMenuSelection {
  value: HierarchyMenuOption;
}

const HierarchyKebabMenu = (props: TableHierarchyGroupingKebabMenuProps) => {
  const {
    handleOnRemoveHierarchy,
    handleOnDuplicateHierarchy,
    removeAllowed,
    duplicationAllowed,
    handleRename,
  } = props;

  // Start of legacy functions / constants declaration
  // INFO: EN-85363 To be deprecated / removed once enable_table_advanced_totaling is set to true permanently
  const handleSelectionLegacy = (selection: HierarchyMenuSelection) => {
    switch (selection.value) {
      case HierarchyMenuOption.DUPLICATE: {
        if (duplicationAllowed) {
          handleOnDuplicateHierarchy();
        }
        break;
      }
      case HierarchyMenuOption.REMOVE: {
        handleOnRemoveHierarchy();
        break;
      }
    }

    return false;
  };

  const renderDuplicateOption = () => {
    return (
      <div role="button" data-testid="duplicate-hierarchy-button">
        <SocrataIcon name={IconName.CopyDocument} />
        <span>{I18n.t('fields.table_hierarchies.menu.duplicate', { scope })}</span>
      </div>
    );
  };

  const renderRemoveOption = () => {
    return (
      <div role="button" className="remove-hierarchy-button" data-testid="remove-hierarchy-button">
        <SocrataIcon name={IconName.Remove} />
        <span>{I18n.t('fields.table_hierarchies.menu.remove', { scope })}</span>
      </div>
    );
  };

  const renderPlaceholder = () => {
    return (
      <button
        className="dropdown-btn socrata-icon-kebab"
        aria-label={I18n.t('fields.table_hierarchies.menu.title', { scope })}
      />
    );
  };

  const optionsLegacy = [
    {
      render: renderDuplicateOption,
      value: HierarchyMenuOption.DUPLICATE,
      disabled: !duplicationAllowed,
    },
    {
      render: renderRemoveOption,
      value: HierarchyMenuOption.REMOVE,
      disabled: !removeAllowed,
    },
  ];

  const dropdownProps = {
    handleSelection: handleSelectionLegacy,
    picklistSizingStrategy: picklistSizingStrategy.EXPAND_TO_WIDEST_ITEM,
    options: optionsLegacy,
    size: DropdownSize.SMALL,
    placeholder: renderPlaceholder,
  };

  const renderLegacyTableHierarchyGroupingKebabMenu = () => (
    <div className="hierarchy-actions-menu">
      <Dropdown {...dropdownProps} />
    </div>
  );
  // End of legacy declarations

  const handleSelection = ({ detail }: CustomEvent<IMenuSelectEventData>) => {
    switch (detail.value) {
      case HierarchyMenuOption.DUPLICATE: {
        if (duplicationAllowed) {
          handleOnDuplicateHierarchy();
        }
        break;
      }
      case HierarchyMenuOption.REMOVE: {
        handleOnRemoveHierarchy();
        break;
      }
      case HierarchyMenuOption.RENAME: {
        if (handleRename) {
          handleRename();
        }
        break;
      }
    }

    return false;
  };

  const options: IMenuOption[] = [
    {
      label: I18n.t('fields.table_hierarchies.menu.rename', { scope }),
      value: HierarchyMenuOption.RENAME,
    },
    {
      label: I18n.t('fields.table_hierarchies.menu.duplicate', { scope }),
      value: HierarchyMenuOption.DUPLICATE,
    },
    {
      label: I18n.t('fields.table_hierarchies.menu.delete', { scope }),
      value: HierarchyMenuOption.REMOVE,
      disabled: !removeAllowed,
    },
  ];

  return isTableAdvancedTotalingEnabled() ? (
    <div className="hierarchy-actions-menu">
      <ForgeMenu on-forge-menu-select={handleSelection} options={options}>
        <ForgeIconButton
          className="dropdown-btn"
          dense={true}
          density="small"
          aria-label={I18n.t('fields.table_hierarchies.menu.title', { scope })}
        >
          <ForgeIcon name="more_vert" />
        </ForgeIconButton>
      </ForgeMenu>
    </div>
  ) : (
    renderLegacyTableHierarchyGroupingKebabMenu()
  );
};

export default HierarchyKebabMenu;
